"use client";

import { useEffect, useState } from "react";

type Check = { ok: boolean; detail?: string };
type HealthResponse = { ok: boolean; checks: Record<string, Check> };

const REFRESH_MS = 30_000;

export function StatusBanner() {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const res = await fetch("/api/health", { cache: "no-store" });
        const data = (await res.json()) as HealthResponse;
        if (cancelled) return;
        setHealth(data);
        // re-show banner if a previously-OK check is now failing
        if (!data.ok) setDismissed(false);
      } catch {
        if (cancelled) return;
        setHealth({
          ok: false,
          checks: { network: { ok: false, detail: "/api/health unreachable" } },
        });
      }
    };

    check();
    const id = setInterval(check, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (!health || health.ok || dismissed) return null;

  const failed = Object.entries(health.checks)
    .filter(([, c]) => !c.ok)
    .map(([name, c]) => `${name}${c.detail ? ` — ${c.detail}` : ""}`)
    .join("; ");

  return (
    <div
      role="alert"
      className="flex items-center gap-3 border-b border-yellow-300 bg-yellow-100 px-6 py-3 text-sm text-yellow-900 dark:border-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-200"
    >
      <span aria-hidden>⚠</span>
      <span className="flex-1">
        Service degraded — {failed}. Some features may not work.
      </span>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="text-yellow-900 hover:opacity-70 dark:text-yellow-200"
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}